import { Copy } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import type { MediaAsset } from '../types/media-library.types';

type InsertSnippetModalProps = {
  asset: MediaAsset | null;
  onClose: () => void;
};

export const InsertSnippetModal = ({ asset, onClose }: InsertSnippetModalProps) => {
  const { toast } = useToast();

  if (!asset) return null;

  const cleanName = asset.name.replace(/\.[a-z0-9]+$/i, '');
  const snippet = `<img src="${asset.url}" alt="${cleanName}">`;

  const handleCopySnippet = async () => {
    try {
      await copyTextToClipboard(snippet);
      toast({
        variant: 'success',
        title: 'Snippet copied.',
      });
      onClose();
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to copy',
        description: 'Could not copy snippet to clipboard.',
      });
    }
  };

  return (
    <Dialog open={Boolean(asset)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md rounded-[12px] p-6">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#1B2021]">
            Insert “{asset.name}”
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <p className="text-[14px] text-[#666666]">
            Paste this into your template or campaign content:
          </p>
          <Textarea
            readOnly
            value={snippet}
            className="h-20 resize-y border-[#DEE0E3] bg-[#FAFAFA] font-mono text-[13px] text-[#1B2021] focus-visible:ring-[#0066B2]"
          />
        </div>

        <DialogFooter className="mt-2 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-[14px] font-medium text-[#666666] hover:bg-[#F4F5F6] hover:text-[#1B2021]"
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={() => void handleCopySnippet()}
            className="gap-1.5 rounded-[6px] bg-[#0066B2] text-[14px] font-medium text-white hover:bg-[#005494]"
          >
            <Copy className="h-4 w-4" />
            <span>Copy snippet</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
